"use client";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  TextareaAutosize,
  Grid,
  Paper,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import axios from "axios";

const shortenFileName = (name, maxLength = 25) => {
  if (name.length <= maxLength) return name;
  const ext = name.substring(name.lastIndexOf("."));
  return name.substring(0, 10) + "..." + ext;
};

const SmsUiUploadForm = () => {
  const [video, setVideo] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [images, setImages] = useState([]);
  const [numbersText, setNumbersText] = useState("");
  const [correctNumbers, setCorrectNumbers] = useState([]);
  const [apiIncorrectNumbers, setApiIncorrectNumbers] = useState([]);
  const [formatIncorrectNumbers, setFormatIncorrectNumbers] = useState([]);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [whatsappBalance, setWhatsappBalance] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumOrDown = useMediaQuery(theme.breakpoints.down("md"));

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        const parsedUser = storedUser ? JSON.parse(storedUser) : null;
        const username = parsedUser?.username || "sagar124";

        const response = await axios.post(
          "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/credit_user",
          { username }
        );

        const body =
          response.data?.["body-json"]?.body || response.data?.body || {};
        const whatsappBal = body?.Items?.[0]?.whatsapp_balance || 0;
        setWhatsappBalance(whatsappBal);
      } catch (error) {
        console.error("Failed to fetch WhatsApp balance:", error);
      }
    };

    fetchBalance();
  }, []);

  const getLocalUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  };

  const validateNumbers = async () => {
    const originalNumbers = numbersText
      .split("\n")
      .map((num) => num.trim())
      .filter((num) => num !== "");

    const formatInvalidNumbers = [];
    const validFormatNumbers = [];

    originalNumbers.forEach((num) => {
      let cleaned = num.trim().replace(/[\s\-\(\)\.]/g, "");

      if (cleaned.startsWith("+91") && cleaned.length === 13) {
        cleaned = cleaned.substring(3);
      } else if (cleaned.startsWith("91") && cleaned.length === 12) {
        cleaned = cleaned.substring(2);
      }

      if (/^\d{10}$/.test(cleaned)) {
        validFormatNumbers.push(cleaned);
      } else {
        formatInvalidNumbers.push(num);
      }
    });

    setFormatIncorrectNumbers(formatInvalidNumbers);

    if (validFormatNumbers.length === 0) {
      showSnackbar(
        "Please enter at least one valid 10-digit phone number.",
        "error"
      );
      return;
    }

    try {
      const response = await axios.post(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/number_correct",
        { number: validFormatNumbers }
      );

      const body =
        response.data?.["body-json"]?.body || response.data?.body || {};

      const correct = body["new correct Number"] || [];
      const apiIncorrect = body["error Number"] || [];

      setCorrectNumbers(correct);
      setApiIncorrectNumbers(apiIncorrect);
      setNumbersText(correct.join("\n"));
      showSnackbar("Numbers validated successfully!", "success");
    } catch (err) {
      console.error("Error validating numbers:", err);
      showSnackbar("Validation failed. Please try again.", "error");
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // ✅ FIXED: Show alert immediately when trying to upload the 5th image
  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const currentImageCount = images.length;
    const totalAfterAdding = currentImageCount + selectedFiles.length;

    // Immediately prevent upload if total exceeds 4
    if (totalAfterAdding > 4) {
      showSnackbar("You can only upload a maximum of 4 images.", "error");
      e.target.value = "";
      return;
    }

    const maxSize = 1 * 1024 * 1024; // 1MB
    let hasOversizedFiles = false;
    let oversizedFileNames = [];

    const validFiles = selectedFiles.filter((file) => {
      if (file.size > maxSize) {
        hasOversizedFiles = true;
        oversizedFileNames.push(file.name);
        return false;
      }
      return true;
    });

    if (hasOversizedFiles) {
      showSnackbar(
        `${oversizedFileNames.join(", ")} exceed 1MB. Please upload smaller files.`,
        "error"
      );
    }

    if (validFiles.length > 0) {
      setImages((prev) => [...prev, ...validFiles]);
      showSnackbar("Image(s) uploaded successfully!", "success");
    }

    e.target.value = "";
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    const syntheticEvent = { target: { files } };
    handleImageChange(syntheticEvent);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      showSnackbar("Video file size must be less than 3MB", "error");
      return;
    }
    setVideo(file);
    showSnackbar("Video uploaded successfully!", "success");
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      showSnackbar("PDF file size must be less than 1MB", "error");
      return;
    }
    setPdf(file);
    showSnackbar("PDF uploaded successfully!", "success");
  };

  const handleSubmit = async () => {
    const user = getLocalUser();
    const username = user?.username || user?.email || "guest";
    const transaction_user = username;

    if (!message || correctNumbers.length === 0) {
      showSnackbar(
        "Please validate numbers and enter a message before submitting.",
        "error"
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const imageBase64List = await Promise.all(
        images.map(async (file) => ({
          data_url: await toBase64(file),
          file: { name: file.name },
        }))
      );

      const payload = {
        remark: "WAPP campaign",
        transaction_user,
        username,
        count: correctNumbers.length.toString(),
        type: "whatsapp_balance",
        imagedata: imageBase64List,
        pdf_name: pdf?.name || null,
        pdf: pdf ? await toBase64(pdf) : null,
        video_name: video?.name || null,
        video: video ? await toBase64(video) : null,
        content: {
          id: "ZmwFmd0Y",
          name: "CMPZmwFmd0Y",
          phone_numbers: correctNumbers,
          message,
        },
      };

      await axios.post(
        "https://d3ioa5kdb9.execute-api.ap-south-1.amazonaws.com/Tis_whatsapp_be/create_camp",
        payload
      );

      showSnackbar("Campaign submitted successfully!", "success");
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      console.error("Submission error:", err);
      showSnackbar("Submission failed. Please try again.", "error");
      setIsSubmitting(false);
    }
  };

  const totalIncorrectNumbers =
    formatIncorrectNumbers.length + apiIncorrectNumbers.length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#e3f2fd", minHeight: "100vh" }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Grid container spacing={4} direction={isMediumOrDown ? "column" : "row"}>
        {/* LEFT PANEL */}
        <Grid item sx={{ width: { xs: "100%", md: 460 } }}>
          <Box
            bgcolor="#e5e1f9"
            p={1}
            borderRadius={2}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <ViewModuleIcon />
            <Box>
              <Typography fontWeight={600}>WB</Typography>
              <Typography>
                {whatsappBalance !== null ? whatsappBalance : "Loading..."}
              </Typography>
            </Box>
          </Box>

          <Box mt={2}>
            <Paper
              sx={{
                p: 2,
                maxHeight: { xs: "unset", md: "90vh" },
                border: "2px solid #ccc",
                borderRadius: 2,
                overflowY: "auto",
              }}
            >
              <Box display="flex" flexDirection="column" gap={1} mb={1}>
                <Typography
                  fontWeight="bold"
                  sx={{ bgcolor: "#c8e6c9", p: 1, borderRadius: 1 }}
                >
                  ✅ Correct Number Count: {correctNumbers.length}
                </Typography>

                <Box sx={{ bgcolor: "#ffcdd2", p: 1, borderRadius: 1 }}>
                  <Typography fontWeight="bold">
                    ❌ Incorrect Number Count: {totalIncorrectNumbers}
                  </Typography>
                </Box>
              </Box>

              <TextField
                multiline
                fullWidth
                minRows={10}
                value={numbersText}
                onChange={(e) => setNumbersText(e.target.value)}
                placeholder={`Enter 10-digit numbers, one per line\nExample:\n9876543210\n9123456789\n+919876543210`}
              />
            </Paper>

            <Box
              mt={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={validateNumbers}
                sx={{ height: "28px", fontSize: "14px", fontWeight: "bold" }}
              >
                Validate Number
              </Button>
              <Typography variant="caption" color="error" textAlign="center">
                * At least one number is required to validate
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* RIGHT PANEL */}
        <Grid item xs sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 3, bgcolor: "#e0f7fa" }}>
            {/* ✅ Message Box */}
            <Box mb={3}>
              <Typography mb={1}>
                Message: {1500 - message.length} characters left
              </Typography>
              <TextareaAutosize
                minRows={5}
                maxLength={1500}
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= 1500) setMessage(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: isSmallScreen ? "10px" : "15px",
                  borderRadius: 4,
                  borderColor: "#ccc",
                  fontSize: "16px",
                  fontFamily: "inherit",
                  backgroundColor: "#fff",
                }}
                placeholder="Paste message with emoji (max 1500 characters)"
              />
            </Box>

            {/* Image Upload */}
            <Box mb={3}>
              <Typography fontWeight="bold" fontSize="16px">
                Select and Upload Image{" "}
                <span style={{ color: "red", fontStyle: "italic" }}>
                  [ max size: 1 MB ] [ max 4 images ]
                </span>
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  mt: 1,
                  p: 3,
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  height: "120px",
                  cursor: "pointer",
                }}
                onClick={() => document.getElementById("img-upl").click()}
                onDrop={handleImageDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <CloudUploadIcon fontSize="large" />
                <Typography ml={2} variant="h6" fontSize="16px">
                  Click or Drop Here
                </Typography>
                <input
                  id="img-upl"
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handleImageChange}
                />
              </Box>

              <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                {images.map((img, i) => (
                  <Paper
                    key={i}
                    sx={{
                      width: 80,
                      height: 80,
                      overflow: "hidden",
                      position: "relative",
                      borderRadius: 1,
                    }}
                    variant="outlined"
                  >
                    <img
                      src={URL.createObjectURL(img)}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setImages((prev) => prev.filter((_, x) => x !== i))
                      }
                      sx={{ position: "absolute", top: 0, right: 0 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            </Box>

            {/* Video & PDF */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ bgcolor: "#f0f6ff", p: 2 }}>
                  <Typography>
                    Upload Video:{" "}
                    <span style={{ color: "red", fontStyle: "italic" }}>
                      [ max size: 3 MB ]
                    </span>
                  </Typography>
                  <Box display="flex" gap={2} mt={1}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadFileIcon />}
                    >
                      Choose Video
                      <input
                        type="file"
                        hidden
                        accept="video/*"
                        onChange={handleVideoChange}
                      />
                    </Button>
                    {video && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          sx={{ maxWidth: 150, wordBreak: "break-word" }}
                        >
                          {shortenFileName(video.name)}
                        </Typography>
                        <IconButton
                          onClick={() => setVideo(null)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ bgcolor: "#f0f6ff", p: 2 }}>
                  <Typography>
                    Upload PDF:{" "}
                    <span style={{ color: "red", fontStyle: "italic" }}>
                      [ max size: 1 MB ]
                    </span>
                  </Typography>
                  <Box display="flex" gap={2} mt={1}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadFileIcon />}
                    >
                      Choose PDF
                      <input
                        type="file"
                        hidden
                        accept="application/pdf"
                        onChange={handlePdfChange}
                      />
                    </Button>
                    {pdf && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          sx={{ maxWidth: 150, wordBreak: "break-word" }}
                        >
                          {shortenFileName(pdf.name)}
                        </Typography>
                        <IconButton
                          onClick={() => setPdf(null)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                height: "28px",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: 3,
              }}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isSubmitting ? "Submitting..." : "Submit Campaign"}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SmsUiUploadForm;
